import {
  ActionIcon,
  Card,
  Group,
  MultiSelect,
  Stack,
  Text,
  Tooltip
} from '@mantine/core'
import { UseFormReturnType, isEmail } from '@mantine/form'
import {
  IconDeviceDesktop,
  IconMail,
  IconTrash,
  IconWebhook
} from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Device } from '@/types/device'
import { MultiValueField } from './MultiValueField'
import { NotificationsFormValues, isValidWebhookUrl } from './notificationRules'

type NotificationRuleCardProps = {
  index: number
  form: UseFormReturnType<NotificationsFormValues>
  devices: Device[]
  isLoadingDevices?: boolean
  canRemove?: boolean
  disabled?: boolean
  onRemove: () => void
  onBlockingDraftChange?: (key: string, blocking: boolean) => void
}

export const NotificationRuleCard = ({
  index,
  form,
  devices,
  isLoadingDevices,
  canRemove,
  disabled,
  onRemove,
  onBlockingDraftChange
}: NotificationRuleCardProps) => {
  const intl = useIntl()

  const rule = form.values.rules[index]

  const emailsPath = `rules.${index}.emails`
  const webhooksPath = `rules.${index}.chat_webhooks`

  const clearRecipientErrors = () => {
    form.clearFieldError(emailsPath)
    form.clearFieldError(webhooksPath)
  }

  return (
    <Card padding="lg" radius="md" withBorder>
      <Stack gap="lg">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            <FormattedMessage
              id="workspace.notifications.rule.title"
              values={{ number: <span>{index + 1}</span> }}
            />
          </Text>

          {canRemove && (
            <Tooltip
              label={
                <FormattedMessage id="workspace.notifications.rule.remove" />
              }
            >
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                radius="xl"
                aria-label={intl.formatMessage({
                  id: 'workspace.notifications.rule.remove'
                })}
                disabled={disabled}
                onClick={onRemove}
              >
                <IconTrash style={{ width: '70%', height: '70%' }} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        <Stack gap={6}>
          <Group gap={6} wrap="nowrap">
            <IconDeviceDesktop size={16} />

            <Text size="sm" fw={500}>
              <FormattedMessage id="workspace.notifications.devices.label" />
            </Text>
          </Group>

          <MultiSelect
            data={devices.map((device) => ({
              value: device.id,
              label: device.label
            }))}
            placeholder={intl.formatMessage({
              id: 'workspace.notifications.devices.placeholder'
            })}
            disabled={disabled || isLoadingDevices}
            nothingFoundMessage={
              <FormattedMessage id="workspace.notifications.devices.empty" />
            }
            searchable
            clearable
            hidePickedOptions
            {...form.getInputProps(`rules.${index}.device_ids`)}
          />
        </Stack>

        <MultiValueField
          label={intl.formatMessage({
            id: 'workspace.notifications.emails.label'
          })}
          removeLabel={intl.formatMessage({
            id: 'workspace.notifications.emails.remove'
          })}
          icon={<IconMail size={16} />}
          placeholder={intl.formatMessage({
            id: 'workspace.notifications.emails.placeholder'
          })}
          description={
            <FormattedMessage id="workspace.notifications.emails.hint" />
          }
          values={rule.emails}
          error={form.errors[emailsPath]}
          disabled={disabled}
          validate={isEmail(
            intl.formatMessage({ id: 'workspace.notifications.emails.invalid' })
          )}
          splitOnComma
          onAdd={(value) => {
            form.insertListItem(emailsPath, value)
            clearRecipientErrors()
          }}
          onRemove={(emailIndex) => form.removeListItem(emailsPath, emailIndex)}
          onBlockingDraftChange={(blocking) =>
            onBlockingDraftChange?.(emailsPath, blocking)
          }
        />

        <MultiValueField
          label={intl.formatMessage({
            id: 'workspace.notifications.webhooks.label'
          })}
          removeLabel={intl.formatMessage({
            id: 'workspace.notifications.webhooks.remove'
          })}
          icon={<IconWebhook size={16} />}
          placeholder={intl.formatMessage({
            id: 'workspace.notifications.webhooks.placeholder'
          })}
          description={
            <FormattedMessage id="workspace.notifications.webhooks.hint" />
          }
          values={rule.chat_webhooks}
          error={form.errors[webhooksPath]}
          disabled={disabled}
          validate={(value) =>
            isValidWebhookUrl(value)
              ? null
              : intl.formatMessage({
                  id: 'workspace.notifications.webhooks.invalid'
                })
          }
          onAdd={(value) => {
            form.insertListItem(webhooksPath, value)
            clearRecipientErrors()
          }}
          onRemove={(webhookIndex) =>
            form.removeListItem(webhooksPath, webhookIndex)
          }
          onBlockingDraftChange={(blocking) =>
            onBlockingDraftChange?.(webhooksPath, blocking)
          }
        />
      </Stack>
    </Card>
  )
}
